'use client';

import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { env } from '@/env';
import { setLogoutHandler } from '@/features/knowledge/lib/api-helpers';
import { Action, matchesPermission, Resource, Scope } from '@/lib/acl';
import { AppError } from '@/lib/error';
import { updateMastraToken } from '@/lib/mastra';

import {
  AuthAdapter,
  AuthCapabilities,
  AuthFlowType,
  AuthState,
  AuthUser,
  CredentialsInput,
  InviteInfo,
  UserPreferences,
  VerifyPasswordResetInput,
} from '../lib/auth-adapter';
import { createAuthAdapter } from '../lib/create-adapter';

export type AuthContextValue = {
  // State
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AppError | null;

  // Adapter info
  flowType: AuthFlowType;
  adapterName: string;
  capabilities: AuthCapabilities;

  // Actions
  signIn: (credentials?: CredentialsInput) => Promise<void>;
  signUp: (credentials: CredentialsInput) => Promise<AuthState>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  verifyResetPasswordCode: (input: VerifyPasswordResetInput) => Promise<string>;
  completePasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  acceptInvite: (userId: string, password: string, email: string) => Promise<void>;
  checkInvite: (userId: string) => Promise<InviteInfo>;
  resetErrors: () => void;

  // Token access for API clients
  getAccessToken: () => Promise<string | null>;

  // ACL
  checkPermission: <TResource extends Resource>(
    resource: TResource,
    action: Action<TResource>,
    scope: Scope,
  ) => boolean;

  // Preferences
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function useAuthAdapter() {
  const adapterRef = useRef<AuthAdapter>(null);
  if (!adapterRef.current) {
    adapterRef.current = createAuthAdapter();
    setLogoutHandler(adapterRef.current.signOut);
  }
  return adapterRef.current;
}

export type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const adapter = useAuthAdapter();
  const [authState, setAuthState] = useState<AuthState>(() => adapter.getAuthState());
  const initialized = useRef(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = adapter.onAuthStateChange((state) => {
      updateMastraToken(state.token?.accessToken);
      setAuthState(state);
    });

    if (!initialized.current) {
      initialized.current = true;
      adapter.initialize();
    }

    return unsubscribe;
  }, [adapter]);

  const checkPermission = useCallback(
    (resource: string, action: string, scope: string) => {
      const permissions = authState.user?.permissions ?? [];
      if (!env.NEXT_PUBLIC_ACL_ENABLED) return true;
      return permissions.some((p) => matchesPermission(p, resource, action, scope));
    },
    [authState.user?.permissions],
  );

  const signOut = useCallback(async () => {
    await adapter.signOut();
    queryClient.cancelQueries();
    queryClient.clear();
  }, [adapter, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: authState.user,
      isAuthenticated: authState.status === 'authenticated',
      isLoading: authState.status === 'loading' || authState.status === 'idle',
      error: authState.error,
      flowType: adapter.flowType,
      adapterName: adapter.name,
      capabilities: adapter.capabilities,
      signIn: adapter.signIn,
      signUp: adapter.signUp,
      signOut: signOut,
      resetPassword: adapter.resetPassword,
      changePassword: adapter.changePassword,
      getAccessToken: adapter.getAccessToken,
      acceptInvite: adapter.acceptInvite,
      checkInvite: adapter.checkInvite,
      verifyResetPasswordCode: adapter.verifyResetPasswordCode,
      completePasswordReset: adapter.completePasswordReset,
      resetErrors: adapter.resetErrors,
      checkPermission,
      updatePreferences: adapter.updatePreferences,
    }),
    [
      authState.user,
      authState.status,
      authState.error,
      adapter.flowType,
      adapter.name,
      adapter.capabilities,
      adapter.signIn,
      adapter.signUp,
      adapter.resetPassword,
      adapter.changePassword,
      adapter.getAccessToken,
      adapter.acceptInvite,
      adapter.checkInvite,
      adapter.verifyResetPasswordCode,
      adapter.completePasswordReset,
      adapter.resetErrors,
      adapter.updatePreferences,
      checkPermission,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
